/**
 * Lo que se ve si el editor falla al dibujarse.
 *
 * Sin esto, un fallo en cualquier panel —un bloque mal formado, un dato que no
 * se esperaba— dejaba la pantalla en blanco, y lo hecho desde el último
 * guardado se perdía. Aquí se vuelca en el acto la copia local (el store sigue
 * vivo aunque React haya fallado) y se ofrece recargar: al volver, el editor
 * ofrece recuperarla.
 *
 * Es una clase porque React sólo atrapa errores de render en una clase. Usa
 * `translate` y no el hook, que aquí no existe.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { VelticaMascot } from '@webbuilder/ui'
import { flushLocalDraft } from '../../lib/localDraft'
import { translate } from '../../lib/i18n'

type State = { failed: boolean; saved: boolean; detail: string }

export class EditorCrashBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false, saved: false, detail: '' }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { failed: true, detail: error instanceof Error ? error.message : String(error) }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('[editor] fallo de render', error, info.componentStack)
    void flushLocalDraft().then(() => this.setState({ saved: true }), () => this.setState({ saved: true }))
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <div className="editor-crash" role="alert">
        <div className="editor-crash-card">
          <div className="editor-crash-brand">
            <span className="editor-crash-mascot"><VelticaMascot size={44} /></span>
            <img src="/veltica.svg" alt="Véltica" decoding="async" />
          </div>
          <h1>{translate('crash.title')}</h1>
          <p>{translate(this.state.saved ? 'crash.safe' : 'crash.saving')}</p>
          <button type="button" className="editor-crash-reload" disabled={!this.state.saved} onClick={() => window.location.reload()}>
            {translate('crash.reload')}
          </button>
          {this.state.detail ? <details><summary>{translate('crash.details')}</summary><code>{this.state.detail}</code></details> : null}
        </div>
      </div>
    )
  }
}
